/**
 * The masthead of a campaign's home screen.
 *
 * The dashboard communicated perfectly and looked like a settings page. This
 * is the fix, and the material is the campaign's own: when a GM has run
 * "Make it make sense", its first illustration becomes the backdrop, so
 * opening Throughline shows you *your* world rather than a product.
 *
 * Every atmospheric choice here is under a scrim and behind a hard rule: the
 * text on top keeps full contrast, because a GM lands on this screen to read
 * numbers and press Start. A hero that costs legibility is a worse dashboard
 * with a picture on it.
 */
import { useEffect, useState } from 'react';

import type { Campaign, SessionHistoryRow } from '@/api/types';
import { getSynthesis, listSessionHistory } from '@/api/client';

function firstLine(text: string): string {
  const lines = (text || '').trim().split('\n');
  const line =
    lines
      .find((l) => l.startsWith('- World, setting, and premise:'))
      ?.replace('- World, setting, and premise:', '')
      .trim() || lines.find((l) => l.trim() && l !== 'CAMPAIGN');
  return line ? line.trim() : '';
}

function ago(iso: string): string {
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 14) return `${days} days ago`;
  if (days < 60) return `${Math.round(days / 7)} weeks ago`;
  return `${Math.round(days / 30)} months ago`;
}

export function CampaignHero({
  campaign,
  presentation,
}: {
  campaign: Campaign | null;
  /** Network-free state for the tutorial film. */
  presentation?: { sessions: SessionHistoryRow[]; art?: string | null };
}) {
  const [localArt, setArt] = useState<string | null>(null);
  const art = presentation ? (presentation.art ?? null) : localArt;
  const [localSessions, setSessions] = useState<SessionHistoryRow[]>([]);
  const sessions = presentation?.sessions ?? localSessions;

  useEffect(() => {
    setArt(null);
    setSessions([]);
    if (!campaign || presentation) return;
    let cancelled = false;

    void Promise.allSettled([
      getSynthesis(campaign.id),
      listSessionHistory(),
    ]).then(([world, history]) => {
      if (cancelled) return;
      if (world.status === 'fulfilled' && world.value.synthesis?.images?.length)
        setArt(world.value.synthesis.images[0]!.url);
      if (history.status === 'fulfilled')
        setSessions(
          history.value.sessions.filter(
            (row) => row.campaign_id === campaign.id,
          ),
        );
    });

    return () => {
      cancelled = true;
    };
  }, [campaign, presentation]);

  if (!campaign) return null;

  // Mic tests are not sessions, here as everywhere else in this app.
  const played = sessions.filter((s) => s.transcript_count >= 40);
  const last = played[0];
  const hours = Math.round(
    played.reduce((total, s) => {
      if (!s.ended_at) return total;
      const ms =
        new Date(s.ended_at).getTime() - new Date(s.started_at).getTime();
      return total + ms / 3_600_000;
    }, 0),
  );

  return (
    <section className={art ? 'hero hero--art' : 'hero'}>
      {art ? (
        <div
          className="hero__art"
          style={{ backgroundImage: `url(${art})` }}
          aria-hidden="true"
        />
      ) : null}
      <div className="hero__body">
        <span className="hero__eyebrow">Your campaign</span>
        <h1 className="hero__title">{campaign.name}</h1>
        {firstLine(campaign.premise) ? (
          <p className="hero__premise">{firstLine(campaign.premise)}</p>
        ) : null}
        <dl className="hero__stats">
          <Stat
            label={played.length === 1 ? 'session' : 'sessions'}
            value={String(played.length)}
          />
          {hours >= 1 ? (
            <Stat label="hours played" value={String(hours)} />
          ) : null}
          {last?.ended_at ? (
            <Stat label="last played" value={ago(last.ended_at)} />
          ) : null}
        </dl>
      </div>
    </section>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="hero__stat">
      <dt className="hero__stat-value">{value}</dt>
      <dd className="hero__stat-label">{label}</dd>
    </div>
  );
}
